import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { getFetchLedgerLot, getFetchLedgerLotHistory } from "../Redux/products/slice";
import { BASE_URL } from '../Redux/apiConfig';
import { Timeline, Text, Title, Center, Button, Loader, Badge, Group, MantineProvider, Card } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDolly, 
  faGears, 
  faCashRegister, 
  faCircleInfo, 
  faFileCirclePlus,
  faHandshake,
  faSignature,
  faFileContract
} from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery } from '@mantine/hooks';

const Column = ({ children }) => (
  <div className="col" style={{ alignItems: "center", justifyContent: "center" }}>{children}</div>
);

const getImages = async (value) => {
  if (!value?.productRefID && !value?.product_name_id) return null;

  const imageUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images/${value.productRefID}.png`;
  const defaultImage = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images/${value.product_name_id}.jpg`; 
  
  try {
    const response = await fetch(imageUrl);
    if (response.ok) {
      return imageUrl;
    }
    // If primary image fails, return default image without checking
    return defaultImage;
  } catch (error) {
    return defaultImage;
  }  
};

const ProductState = ({ product }) => {
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchImage = async () => {
      if (!product?.result?.ProductID) return;

      const url = await getImages({
        productRefID: product.result.ProductID,
        product_name_id: product.result.ProductNameId
      });
      
      if (isMounted) {
        setImageUrl(url);
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
    };
  }, [product?.result?.ProductID, product?.result?.ProductNameId]);

  const getTranslationKey = (key) => {
    const keyMap = {
      "Owner Address": "ownerAddress",
      "Product Name": "productName",
      "UPC": "upc",
      "PhysicalState": "physicalState",
      "Quantity": "quantity",
      "PhysicalStatePeriod": "physicalStatePeriod",
      "PhysicalStateLocation": "physicalStateLocation"
    };
    return keyMap[key] || key;
  };

  const ownerDetails = {
    "Owner Address": product?.result?.OwnerPublicAddress,
    "Product Name": product?.result?.ProductName,
    "UPC": product?.result?.UPC,
  };

  const excludedFields = [
    "OwnerPublicAddress", 
    "ProductName", 
    "UPC", 
    "ProductID", 
    "ParentProductID", 
    "SKU", 
    "darkmode", 
    "ProductNameId"
  ];

  const productSpecifications = Object.entries(product?.result || {})
    .filter(([key]) => !excludedFields.includes(key))
    .map(([key, value]) => ({ label: key, value }));

  // Helper function to get translation or fall back to the original label
  const intl = useIntl();
  const getTranslatedLabel = (label) => {
    const translationKey = `explorer.specification.${getTranslationKey(label)}`;
    
    // Check if translation exists
    const hasTranslation = intl.messages[translationKey] !== undefined;
    
    if (hasTranslation) {
      return <FormattedMessage id={translationKey} />;
    } else {
      // Fall back to the original label if translation doesn't exist
      return label;
    }
  };

  return (
    <div style={{ marginTop: "20px", maxWidth: "600px", margin: "auto" }}>
      <Card withBorder radius="md" style={{ marginBottom: "20px" }}>
        <Card.Section>
          <Title order={4} style={{ padding: "10px 20px" }}>
          {<FormattedMessage id="explorer.owner.title" />}
          </Title>
        </Card.Section>
        <Group direction="column" spacing="sm" style={{ padding: "20px" }}>
          {Object.entries(ownerDetails).map(([key, value]) => (
            <div key={key}>
              <Text size="sm" weight={500}>
                <FormattedMessage id={`explorer.owner.${getTranslationKey(key)}`} />:
              </Text>
              <Text size="sm" color="dimmed">
                {value || "N/A"}
              </Text>
            </div>
          ))}
        </Group>
        {imageUrl && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: "10px" }}>
            <img style={{        
              width: '200px', 
              height: '200px', 
              borderRadius: '12px',
              objectFit: 'cover',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',}} 
              src={imageUrl} 
              alt="Product" />
          </div>
        )}
      </Card>

      <Card withBorder radius="md">
        <Card.Section>
          <Title order={4} style={{ padding: "10px 20px" }}>
            {<FormattedMessage id="explorer.specification.title" />}
          </Title>
        </Card.Section>
        <Group direction="column" spacing="sm" style={{ padding: "20px" }}>
          {productSpecifications.map(({ label, value }) => (
            <div key={label}>
              <Text size="sm" weight={500}>
                {getTranslatedLabel(label)}:
              </Text>
              <Text size="sm" color="dimmed">
                {value || "N/A"}
              </Text>
            </div>
          ))}
        </Group>
      </Card>
    </div>
  );
};

function DualTimeline({ productHist }) {
  const transactionMethods = ['addCommercialContract', 'liftAllSubject', 'sellerSign', 'buyerSign'];
  const network = BASE_URL.includes('vertobackendappdev') ? 'testnet' : 'mainnet';
  const isMobile = useMediaQuery('(max-width: 768px)');

  const processEvents = () => {
    if (!productHist || typeof productHist !== 'object') {
      console.error("productHist is undefined or not an object:", productHist);
      return [];
    }
  
    const allEvents = [];
  
    Object.entries(productHist).forEach(([timestamp, item], index) => {
      let method;
      if (index === 0) {
        method = "productcreation";
      } else if (item.method || item.event) {
        method = item.method || item.event;
      } else if (!item.physicalState || item.physicalState==='') {
        method = "transportLot";
      } else {
        method = "transformLot";
      }
      if (!method) return;
  
      const eventData = {
        timestamp,
        method,
        period: item.Period || item.period,
        location: item.location,
        physicalState: item.physicalState,
        id: item.id,
        transportDescription: item.transportDescription,
        ownerAccountId: item.ownerAccountId,
        _ts: item._ts,
        isTransaction: transactionMethods.includes(method),
        price: item.price
      };
  
      allEvents.push(eventData);
    });
  
    return allEvents.sort((a, b) => (a._ts || 0) - (b._ts || 0));
  };  

  const sortedEvents = processEvents();

  const getIcon = (method, isTransaction) => {
    let icon;
    if (isTransaction) {
      switch (method) {
        case 'addCommercialContract':
          icon = faFileContract;
          break;
        case 'sellerSign':
        case 'buyerSign':
          icon = faSignature;
          break;
        case 'liftAllSubject':
          icon = faHandshake;
          break;
        default:
          icon = faCircleInfo;
      }
    } else {
      switch (method) {
        case 'productcreation':
          icon = faFileCirclePlus;
          break;
        case 'transform':
        case 'transformLot':
          icon = faGears;
          break;
        case 'transport':
        case 'transportLot':
          icon = faDolly;
          break;
        case 'transferOwner':
          icon = faCashRegister;
          break;
        default:
          icon = faCircleInfo;
      }
    }
    return icon;
  };

  const renderTimelineItem = (item, index) => (
    <Timeline.Item
      key={index}
      bullet={
        <FontAwesomeIcon 
          icon={getIcon(item.method, item.isTransaction)} 
          style={{ color: item.isTransaction ? 'white' : undefined }} 
        />
      }
      title={<FormattedMessage id={`explorer.timeline.${item.method}`} />}
      color={item.isTransaction ? "orange" : "blue"}
    >
      <Text color="dimmed" size="sm">
        {item._ts && (
          <>
            <FormattedMessage id="product.dateon1" /> {new Date(item._ts * 1000).toLocaleString()}
            <br />
          </>
        )}
        {item.physicalState && !item.isTransaction && (
          <>
            <FormattedMessage id="explorer.timeline.process" /> {item.physicalState}
            <br />
          </>
        )}
        {item.period && (
          <>
            <FormattedMessage id="product.period1" /> {item.period}
            <br />
          </>
        )}
        {item.location && !item.isTransaction && (
          <>
            <FormattedMessage id="product.location1" /> {item.location}
            <br />
          </>
        )}
        {item.transportDescription && !item.isTransaction && (
          <>
            <FormattedMessage id="product.description1" /> {item.transportDescription}
            <br />
          </>
        )}
        {item.id && (
          <Button
            component="a"
            href={`https://hashscan.io/${network}/${item.ownerAccountId ? 'account' : 'transaction'}/${item.ownerAccountId || item.id}`}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            variant="subtle"
            color={item.isTransaction ? "orange" : "blue"}
          >
            <FormattedMessage id="certificate.linktoledger.hedera" />
          </Button>
        )}
      </Text>
    </Timeline.Item>
  );

  const titleStyle = {
    fontFamily: 'Montserrat, sans-serif',
    marginBottom: '2rem',
    marginTop: '1.5rem'
  };

  const renderChronologicalTimeline = () => {
    const allEvents = sortedEvents.map((item, index) => ({
      ...item,
      position: item.isTransaction ? 'right' : 'left'  // Ensure transactions go right
    }));

    return (
      <div style={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Column Headers */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '2rem'
        }}>
          <div style={{ width: '45%', textAlign: 'center' }}>
            <h5 style={titleStyle}>
              <FormattedMessage id="certificate.history" />
            </h5>
          </div>
          <div style={{ width: '45%', textAlign: 'center' }}>
            <h5 style={titleStyle}>
              <FormattedMessage id="button.comercontract" />:
            </h5>
          </div>
        </div>

        {/* Timeline Content */}
        <div style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'center'
        }}>
          {/* Central line */}
          <div style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: '2px',
            backgroundColor: '#e9ecef',
            transform: 'translateX(-50%)'
          }} />

          <div style={{ width: '100%' }}>
            {allEvents.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: item.position === 'right' ? 'flex-end' : 'flex-start',
                  padding: '20px 0',
                  position: 'relative',
                  width: '100%'
                }}
              >
                <div
                  style={{
                    width: '45%',
                    margin: item.position === 'right' ? '0 0 0 10px' : '0 10px 0 0',
                    position: 'relative'
                  }}
                >
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '15px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    position: 'relative'
                  }}>
                    {/* Rest of the content remains the same */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px'
                    }}>
                      <FontAwesomeIcon 
                        icon={getIcon(item.method, item.isTransaction)} 
                        style={{ 
                          color: 'white',
                          backgroundColor: item.isTransaction ? '#fd7e14' : '#228be6',
                          padding: '8px',
                          borderRadius: '50%',
                          marginRight: '10px'
                        }} 
                      />
                      <h6 style={{ margin: 0 }}>
                        <FormattedMessage id={`explorer.timeline.${item.method}`} />
                      </h6>
                    </div>

                    <Text color="dimmed" size="sm">
                      {item._ts && (
                        <>
                          <FormattedMessage id="product.dateon1" /> {new Date(item._ts * 1000).toLocaleString()}
                          <br />
                        </>
                      )}
                      {item.physicalState && !item.isTransaction && (
                        <>
                          <FormattedMessage id="explorer.timeline.process" /> {item.physicalState}
                          <br />
                        </>
                      )}
                      {item.period && (
                        <>
                          <FormattedMessage id="product.period1" /> {item.period}
                          <br />
                        </>
                      )}
                      {item.location && !item.isTransaction && (
                        <>
                          <FormattedMessage id="product.location1" /> {item.location}
                          <br />
                        </>
                      )}
                      {item.transportDescription && !item.isTransaction && (
                        <>
                          <FormattedMessage id="product.description1" /> {item.transportDescription}
                          <br />
                        </>
                      )}
                      {item.isTransaction && (
                        <>
                          <FormattedMessage id="offer1.priceTitle" /> {item.Price || '-'}
                          <br />
                        </>
                      )}
                      {item.id && (
                        <Button
                          component="a"
                          href={`https://hashscan.io/${network}/${item.ownerAccountId ? 'account' : 'transaction'}/${item.ownerAccountId || item.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="xs"
                          variant="subtle"
                          color={item.isTransaction ? "orange" : "blue"}
                        >
                          <FormattedMessage id="certificate.linktoledger.hedera" />
                        </Button>
                      )}
                    </Text>
                  </div>

                  {/* Timeline connector and dot */}
                  <div style={{
                    position: 'absolute',
                    top: '30px',
                    [item.isTransaction ? 'left' : 'right']: '-6px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: item.isTransaction ? '#fd7e14' : '#228be6',
                    border: '2px solid white',
                    zIndex: 2
                  }} />
                  
                  {/* Horizontal connector line */}
                  <div style={{
                    position: 'absolute',
                    top: '35px',
                    [item.isTransaction ? 'left' : 'right']: '5px',
                    width: '10px',
                    height: '2px',
                    backgroundColor: item.isTransaction ? '#fd7e14' : '#228be6'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      padding: '20px'
    }}>
      <div style={{
        width: isMobile ? '100%' : '70%',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        padding: '20px'
      }}>
        {isMobile ? (
          // Mobile view remains the same
          <div style={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '10px'
          }}>
            <h5 style={titleStyle}>
              <FormattedMessage id="certificate.history" />
            </h5>
            <Timeline 
              active={sortedEvents.length}
              bulletSize={20} 
              lineWidth={2}
              styles={(theme) => ({
                root: {
                  paddingTop: '1rem',
                  paddingBottom: '2rem'
                },
                item: {
                  backgroundColor: 'transparent',
                  paddingBottom: '10px',
                  minHeight: '180px',
                }
              })}
            >
              {sortedEvents.map((item, index) => renderTimelineItem(item, index))}
            </Timeline>
          </div>
        ) : (
          // New chronological desktop view
          <div style={{
            width: '100%',
            padding: '10px'
          }}>
            {renderChronologicalTimeline()}
          </div>
        )}
      </div>
    </div>
  );
}

const CertificatePage = ({ productId, fromExplorer = false }) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [productDict, setProductDict] = useState({});
  const [productHist, setProductHist] = useState({});
  const dispatch = useDispatch();
  const fetchLedgerLot = useSelector((state) => state.products.fetchLedgerLot);
  const fetchLedgerLotHistory = useSelector((state) => state.products.fetchLedgerLotHistory);
  const intl = useIntl();
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState("");
  const [isSpecialAccount, setIsSpecialAccount] = useState(false);
  const selectedLanguage = useSelector((state) => state.language);

  // Use either URL param or prop
  const effectiveId = fromExplorer ? productId : id;

  useEffect(() => {
    const fetchData = async () => {
      if (!effectiveId) return;

      // Only fetch if we don't have data or if the ID changed
      if (!fetchLedgerLot?.result?.ProductID || 
          !fetchLedgerLotHistory?.result ||
          String(fetchLedgerLot.result.ProductID) !== String(effectiveId)) {
        
        setIsLoading(true);
        try {
          await dispatch(getFetchLedgerLot({ inputValue: effectiveId }));
          await dispatch(getFetchLedgerLotHistory({ inputValue: effectiveId }));
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [effectiveId, dispatch]);

  // Process data after fetch
  useEffect(() => {
    if (!fetchLedgerLot?.result) return;

    const product = fetchLedgerLot.result;
    
    setIsSpecialAccount(product?.darkmode === 1);

    if (product?.ProductName) {
      const [mainProductName, ...rest] = product.ProductName.split(",");
      const additionalFields = {};

      if (rest.length > 0) {
        rest.join(",").split("\\r\\n").forEach((line) => {
          if (!line) return;
          const [key, value] = line.split(":").map((str) => str?.trim());
          if (key && value) {
            additionalFields[key] = value;
          }
        });
      }

      setProductDict({
        ...fetchLedgerLot,
        result: {
          ...product,
          ProductName: mainProductName?.trim() || product.ProductName,
          ...additionalFields,
        },
      });
    } else {
      setProductDict(fetchLedgerLot);
    }

    if (fetchLedgerLotHistory) {
      setProductHist(fetchLedgerLotHistory);
    }

    if (product.ProductID) {
      setQrCodeImageUrl(`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/qrcode/${product.ProductID}.png`);
    }
  }, [fetchLedgerLot, fetchLedgerLotHistory]);

  useEffect(() => {
    const fetchData = async () => {
      if (productDict && productDict.result && productDict.result.ProductID) {
        try {
          const productResponse = await fetch(`${BASE_URL}/getProductCertificate?product_id=${productDict.result.ProductID}&targetLanguage=${selectedLanguage}`);
  
          if (productResponse.ok) {
            const qrCodeUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/qrcode/${productDict.result.ProductID}.png`;
            setQrCodeImageUrl(qrCodeUrl);
          }
        } catch (error) {
          console.error(error);
        }
        setIsLoading(false);
      }
    };
  
    fetchData();
  }, [dispatch, productDict, selectedLanguage]);

  return (
    <MantineProvider theme={{ colorScheme: isSpecialAccount ? 'dark' : 'light' }}>
      <div
        style={{
          padding: '30px',
          background: isSpecialAccount
            ? 'linear-gradient(to bottom, #121212, #1A1B1E)'
            : '#f9f9f9',
          minHeight: '100vh',
        }}
      >
        <Title
          order={3}
          weight={200}
          align="center"
          style={{
            color: isSpecialAccount ? '#FFA726' : '#333',
            marginBottom: '20px',
          }}
        >
          {intl.formatMessage({ id: 'explorer.title' })}
        </Title>
        <Center>
          {isLoading ? (
            <Loader />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: "20px" }}>
              {qrCodeImageUrl && (
                <img src={qrCodeImageUrl} alt="QR Code" style={{ maxWidth: '100%' }} />
              )}
              <Column>
                <Title
                  order={3}
                  weight={200}
                  align="center"
                  style={{
                    marginTop: '20px',
                    marginBottom: '30px',
                    color: isSpecialAccount ? '#FFA726' : '#333',
                    fontSize: isSpecialAccount ? '1.8rem' : '1.5rem',
                  }}
                >
                  {<FormattedMessage id="explorer.section.pinfo" />}
                </Title>
                <ProductState product={productDict} />
              </Column>
              <Column>
                <Title
                  order={3}
                  weight={200}
                  align="center"
                  style={{
                    marginTop: '20px',
                    marginBottom: '20px',
                    color: isSpecialAccount ? '#FFA726' : '#333',
                    fontSize: isSpecialAccount ? '1.8rem' : '1.5rem',
                  }}
                >
                  {<FormattedMessage id="explorer.section.phistory" />}
                </Title>
                <DualTimeline productHist={productHist} />
              </Column>
            </div>
          )}
        </Center>
      </div>
    </MantineProvider>
  );
};

export default CertificatePage;